<script setup lang="ts">
import type { CSSProperties } from 'vue'
import BaseResponsiveImages from '~/components/ui/BaseResponsiveImages/BaseResponsiveImages.vue'
import DJIcon from '~/components/ui/icon/Icon.vue'
import { IconUserCircle } from '~/components/ui/icon/icons'

defineOptions({
  name: 'DJAvatar',
})
const props = defineProps({
  /**
   * @description avatar text
   */
  name: {
    type: String,
    default: '',
  },
  /**
   * @description avatar circle shape
   */
  circle: {
    type: Boolean,
    default: true,
  },
  /**
   * @description avatar circle shape
   */
  size: {
    type: Number,
    default: 48,
  },
  /**
   * @description the source of the image for an image avatar
   */
  src: {
    type: Object,
    default: null,
  },
  /**
   * @description native attribute alt of image avatar
   */
  alt: {
    type: String,
    default: '',
  },
})
const emit = defineEmits(['error'])

const hasLoadError = ref(false)

const initials = computed<string>(() =>
  props.name
    .split(' ')
    .map((name) => name[0])
    .join('')
    .replace(/[^\w\s]/gi, '')
    .toUpperCase(),
)
const style = computed<CSSProperties>(() => {
  return {
    '--dj-avatar-size': `${props.size}px`,
  }
})

// need reset hasLoadError to false if src changed
watch(
  () => props.src,
  () => (hasLoadError.value = false),
)

function handleError(e: Event) {
  hasLoadError.value = true
  emit('error', e)
}
function mapCircle(circle: boolean) {
  return circle ? 'is-circle' : ''
}
</script>

<template>
  <span class="dj-avatar" :class="[mapCircle(circle)]" :style="style" v-bind="$attrs">
    <BaseResponsiveImages customClass="hp-100 wp-100" v-if="src && !hasLoadError" :src="src" :alt="alt" @error="handleError" />
    <span class="dj-avatar--name" v-else-if="name && initials">{{ initials }}</span>
    <DJIcon v-else :size="size">
      <IconUserCircle />
    </DJIcon>
  </span>
</template>

<style lang="scss" scoped>
.dj-avatar {
  --dj-avatar-size: 48px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  text-align: center;
  overflow: hidden;
  width: var(--dj-avatar-size);
  height: var(--dj-avatar-size);
  border-radius: calc(var(--dj-avatar-size) * 0.3);
  box-shadow: 0px 4px 44px rgba(0, 0, 0, 0.1);

  &:has(.dj-avatar--name) {
    color: brandColor(brand, 950);
    background: brandColor(gray, 100);
    border: 1.5px solid brandColor(gray, 100);
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.48px;
  }

  & > img {
    display: block;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
  }

  &.is-circle {
    border-radius: 50%;
  }
}
</style>
